import { motion } from "framer-motion";
import { Wand2, AudioWaveform, Sparkles, Check } from "lucide-react";
import SceneShell from "../components/SceneShell";

/**
 * Scene 13 — Section 3 opener · PresenTour + NarraTour intro.
 * Feature colour: #e50064 (PresenTour / NarraTour brand pink).
 *
 * Big stylised wordmarks for both products + the headline quote
 * "Tour scripts and property updates… generated in seconds." +
 * three benefits + the throwaway aside about "no more eggy presenters".
 */

const PINK = "#e50064";
const PINK_GRADIENT = "linear-gradient(135deg, #e50064 0%, #ff2e8a 100%)";

const benefits = [
  { label: "On-brand", note: "Always uses your tone of voice" },
  { label: "Structured", note: "Hook → highlights → call-to-action" },
  { label: "Ready to use", note: "Presenter-ready, voice-ready, no edits needed" },
];

/** Stylised inline wordmark — single line "PresenTour" / "NarraTour" with the
 *  "Tour" portion picked out in the brand pink. */
function TourLogo({ prefix, icon: Icon, testId }) {
  return (
    <div
      className="select-none flex flex-col items-center gap-4"
      data-testid={testId}
    >
      <div
        className="w-14 h-14 rounded-full flex items-center justify-center"
        style={{
          background: "rgba(229,0,100,0.15)",
          border: "1px solid rgba(229,0,100,0.45)",
          color: PINK,
        }}
      >
        <Icon className="w-7 h-7" />
      </div>
      <div
        className="kn-display leading-none whitespace-nowrap"
        style={{
          fontWeight: 900,
          letterSpacing: "-0.04em",
          fontSize: "clamp(48px, 6.4vw, 116px)",
        }}
      >
        <span style={{ color: "#e2e8f0" }}>{prefix}</span>
        <span
          style={{
            background: PINK_GRADIENT,
            WebkitBackgroundClip: "text",
            backgroundClip: "text",
            color: "transparent",
          }}
        >
          Tour
        </span>
      </div>
    </div>
  );
}

export default function Scene13_TourIntro() {
  return (
    <SceneShell variant="ink" align="center" data-testid="scene-tour-intro">
      {/* Pink ambient backlight */}
      <motion.div
        className="absolute inset-0 pointer-events-none"
        animate={{
          background: [
            "radial-gradient(ellipse at 30% 50%, rgba(229,0,100,0.18) 0%, transparent 55%)",
            "radial-gradient(ellipse at 70% 50%, rgba(229,0,100,0.18) 0%, transparent 55%)",
            "radial-gradient(ellipse at 30% 50%, rgba(229,0,100,0.18) 0%, transparent 55%)",
          ],
        }}
        transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
      />

      <div className="relative max-w-[1280px] mx-auto w-full">
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="flex items-center justify-center gap-3 mb-6"
        >
          <span
            className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[11px] font-bold tracking-[0.18em] uppercase"
            style={{
              background: "rgba(229,0,100,0.16)",
              border: "1px solid rgba(229,0,100,0.45)",
              color: PINK,
            }}
            data-testid="tour-intro-badge"
          >
            <Sparkles className="w-3 h-3" />
            Two new products
          </span>
        </motion.div>

        {/* Two big wordmarks */}
        <div className="flex flex-wrap items-center justify-center gap-x-12 gap-y-10 mb-14">
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.92 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 1.0, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
          >
            <TourLogo prefix="Presen" icon={Wand2} testId="logo-presentour" />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.92 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 1.0, delay: 0.7, ease: [0.16, 1, 0.3, 1] }}
          >
            <TourLogo prefix="Narra" icon={AudioWaveform} testId="logo-narratour" />
          </motion.div>
        </div>

        {/* Headline quote */}
        <motion.blockquote
          initial={{ opacity: 0, y: 32, filter: "blur(8px)" }}
          animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
          transition={{ duration: 1.2, delay: 1.1, ease: [0.16, 1, 0.3, 1] }}
          className="text-center mx-auto max-w-4xl"
          data-testid="tour-intro-quote"
        >
          <div
            className="kn-display text-3xl sm:text-4xl lg:text-5xl text-white"
            style={{ lineHeight: 1.18 }}
          >
            "Tour scripts and property updates…
            <br />
            <span
              style={{
                background: PINK_GRADIENT,
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                color: "transparent",
              }}
            >
              generated in seconds.
            </span>
            "
          </div>
        </motion.blockquote>

        {/* Three benefits as horizontal pills */}
        <div className="flex flex-wrap items-center justify-center gap-4 mt-10">
          {benefits.map((b, i) => (
            <motion.div
              key={b.label}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                delay: 1.6 + i * 0.18,
                duration: 0.7,
                ease: [0.16, 1, 0.3, 1],
              }}
              className="flex items-center gap-3 rounded-full px-5 py-2.5"
              style={{
                background: "rgba(255,255,255,0.05)",
                border: "1px solid rgba(229,0,100,0.32)",
              }}
              data-testid={`tour-intro-benefit-${i}`}
            >
              <span
                className="w-6 h-6 rounded-full flex items-center justify-center"
                style={{ background: "rgba(229,0,100,0.20)", color: PINK }}
              >
                <Check className="w-3.5 h-3.5" />
              </span>
              <span className="font-semibold text-white">{b.label}</span>
              <span className="text-white/50 text-sm hidden sm:inline">
                · {b.note}
              </span>
            </motion.div>
          ))}
        </div>
      </div>
    </SceneShell>
  );
}
